import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ROUTE_CLUBS, ROUTE_COACHES } from '@/constants/Routes'
import { BenefitClaimViewWithKind, CLUB_TARGET_KIND, PARTNER_TARGET_KIND } from '@/services/myBenefits'

/**
 * Where a benefit went, and the way back to whoever is honoring it.
 *
 * The line existed as flat text, which was worst exactly where it mattered most: a claim waiting on
 * a coach names the person the member is waiting for, and naming someone with no way to reach them
 * is a dead end on the one screen where the member has a question.
 *
 * The destination differs by kind, which is why the server sends one — a club has a club page, a
 * coach has a coaching profile, and the two are not interchangeable.
 *
 * Falls back to plain text rather than to a guess when the kind is missing or unrecognized. A
 * client talking to an older server, or a newer one carrying a kind this build has never heard of,
 * should say where the benefit went and stop: a link to the wrong page is worse than no link, and
 * "club" is the wrong page for a coach.
 *
 * Mirrors `ClaimTargetLink.tsx` in the mobile app.
 */
export const ClaimTargetLink = ({ claim }: { claim: BenefitClaimViewWithKind }) => {
  const { t } = useTranslation()
  const label = t('benefits.claimedAt', { targetName: claim.targetName })
  const href = destinationFor(claim)

  if (!href) return <>{label}</>

  return (
    <Link to={href} className="text-[#2D5FB8] underline-offset-2 hover:underline">
      {label}
    </Link>
  )
}

/** Where clicking should go, or null when there is nothing safe to link to. */
const destinationFor = (claim: BenefitClaimViewWithKind): string | null => {
  const kind = claim.targetKind
  if (!kind || kind === 'unknown') return null
  if (kind === CLUB_TARGET_KIND) return `${ROUTE_CLUBS}/${claim.targetId}`
  // A partner has no page here. Its link is the redemption block's, which opens off DUPR — never
  // a coach profile, which is what the entity fallback below would build.
  if (kind === PARTNER_TARGET_KIND) return null
  // Every other kind is a platform entity, and the profile route is addressed by kind and id —
  // which is the whole reason the kind is on the wire rather than inferred from the URN.
  return `${ROUTE_COACHES}/${kind}/${claim.targetId}`
}
